// app/(app)/inventory/stock/[id].tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearError,
  deleteStockEntry,
  fetchStockEntryById,
  resetSelected,
  restoreStockEntry,
} from "@/store/slices/stockSlice";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect } from "react";
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function StockDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { selectedEntry, loading, updating, error } = useAppSelector(
    (state) => state.stock,
  );
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = user?.userType === "Owner";

  useEffect(() => {
    dispatch(fetchStockEntryById(Number(id)));
    return () => {
      dispatch(resetSelected());
    };
  }, [id]);

  useEffect(() => {
    if (error) {
      Alert.alert("Error", error);
      dispatch(clearError());
    }
  }, [error]);

  const handleDelete = () => {
    Alert.alert("Delete", "Soft delete this transaction? (Owner only)", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          const result = await dispatch(deleteStockEntry(Number(id)));
          if (deleteStockEntry.fulfilled.match(result)) router.back();
        },
      },
    ]);
  };

  const handleRestore = () => {
    Alert.alert("Restore", "Restore this soft-deleted transaction?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Restore",
        onPress: async () => {
          const result = await dispatch(restoreStockEntry(Number(id)));
          if (restoreStockEntry.fulfilled.match(result)) router.back();
        },
      },
    ]);
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.lg,
    },
    infoRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.sm,
    },
    label: { fontSize: 14, color: colors.textLight },
    value: { fontSize: 14, color: colors.text, fontWeight: "500" },
    deleteButton: { marginTop: spacing.lg },
    restoreButton: { marginTop: spacing.md },
  });

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }
  if (!selectedEntry) return null;

  const isDeleted = !!(selectedEntry as { deletedAt?: string | null })
    .deletedAt;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
    >
      <Text style={styles.title}>Transaction Details</Text>
      <View style={styles.infoRow}>
        <Text style={styles.label}>ID:</Text>
        <Text style={styles.value}>{selectedEntry.id}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Product:</Text>
        <Text style={styles.value}>
          {selectedEntry.Product?.name || `ID: ${selectedEntry.Product.id}`}
        </Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Type:</Text>
        <Text
          style={[
            styles.value,
            { color: selectedEntry.type === "in" ? "#22c55e" : "#ef4444" },
          ]}
        >
          {selectedEntry.type.toUpperCase()}
        </Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Quantity:</Text>
        <Text style={styles.value}>{selectedEntry.quantity}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Reason:</Text>
        <Text style={styles.value}>{selectedEntry.reason}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Created By:</Text>
        <Text style={styles.value}>
          {selectedEntry.creator?.ownerName || `User ${selectedEntry.creator}`}
        </Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Created At:</Text>
        <Text style={styles.value}>
          {new Date(selectedEntry.createdAt).toLocaleString()}
        </Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.label}>Status:</Text>
        <Text
          style={[styles.value, { color: isDeleted ? "#ef4444" : "#22c55e" }]}
        >
          {isDeleted ? "DELETED" : "ACTIVE"}
        </Text>
      </View>

      {/* {isOwner && (
        <>
          {!isDeleted ? (
            <Button
              title="Soft Delete Transaction"
              onPress={handleDelete}
              variant="danger"
              containerStyle={styles.deleteButton}
              loading={updating}
            />
          ) : (
            <Button
              title="Restore Transaction"
              onPress={handleRestore}
              variant="primary"
              containerStyle={styles.restoreButton}
              loading={updating}
            />
          )}
        </>
      )} */}
      {/* {!isOwner && (
        <Text
          style={{
            color: colors.textLight,
            marginTop: spacing.lg,
            textAlign: "center",
          }}
        >
          Only owners can delete or restore transactions.
        </Text>
      )} */}
    </ScrollView>
  );
}
